<!-- The tab shell. THIS FILE IS YOURS.

     IonTabs wraps its OWN IonRouterOutlet, and that is what gives each tab a
     separate navigation stack: pushing /tabs/items/3 leaves the tab bar in
     place with a working back button, and switching away and back returns you
     to the detail screen rather than the top of the list. A single top-level
     outlet with a tab bar beside it cannot do that — it has one stack, so
     every tab switch is a replace and every push loses the bar.

     TWO PROPS ON EVERY TAB BUTTON, AND BOTH ARE LOAD-BEARING:
       `tab`   the id IonTabs tracks it by
       `href`  what IonTabs matches against the current route to decide which
               button is active

     The href is NOT followed as a link — IonTabButton calls
     navManager.changeTab(), which pushes through the router — so it works
     under the memory history this app uses. A button without an href is a tab
     that navigates fine and never highlights, which is a confusing five
     minutes to debug because nothing errors.

     THE IonPage WRAPPER IS REQUIRED. IonTabs is not itself a page, so a shell
     whose root is IonTabs gives the PARENT outlet nothing with `.ion-page` to
     transition, and Ionic warns "the view you are trying to render does not
     have the required <ion-page> component" on every tab change. It renders
     anyway, which is why this is easy to leave broken: what you lose is the
     enter/leave animation and the page lifecycle events.

     `work-up` ON THE SHELL'S PAGE TOO. Every screen carried the class and the
     shell did not, so anything the shell draws itself — the tab bar — sat
     outside the app's own rules. The palette now lives on :root (app.css) so
     nothing can fall through that gap again; the class is for the page-level
     rules. -->
<template>
  <IonPage class="work-up">
    <IonTabs>
      <IonRouterOutlet />

      <!-- A ONE-BUTTON TAB BAR IS FURNITURE.
           It says "you are where you already are" while taking a permanent
           strip off the bottom of every screen — the exact cost the bottom-tabs
           shell is meant to buy something with. One real app shipped with
           exactly one destination and a tab bar under it.
           Below two, the bar is not rendered and `--tabbar-clearance` goes to
           zero, so the content reclaims the space rather than leaving a gap
           where a bar used to be. -->
      <IonTabBar v-if="hasBar" slot="bottom">
        <!-- A FLAT v-for, NOT a <template v-for>. IonTabs reads its slot
             children directly to build its tab bookkeeping and does not unwrap
             fragments, so wrapping these makes every child a Fragment vnode and
             the whole thing throws `Cannot read properties of undefined`. -->
        <IonTabButton
          v-for="t in tabs"
          :key="t.name"
          :tab="t.name"
          :href="t.path"
        >
          <IonIcon :icon="t.icon" aria-hidden="true" />
          <IonLabel>{{ t.label }}</IonLabel>
        </IonTabButton>

      </IonTabBar>
    </IonTabs>
  </IonPage>
</template>

<script setup>
import { onMounted, onBeforeUnmount } from 'vue';
import {
  IonPage, IonTabs, IonRouterOutlet, IonTabBar, IonTabButton, IonIcon, IonLabel,
} from '@ionic/vue';
import { TABS } from '../router.js';

// THE BAR'S ORDER IS A DESIGN DECISION, not the plan's. TABS follows the
// order screens were added to the product plan (Lifts, Timeline, Bar, Kit);
// the bar reads Lifts · Bar · Timeline · Kit — the two things you do at the
// rack first, the record after, the settings last. Keyed by screen id, and any
// tab not named here keeps its plan order after these.
const ORDER = ['lifts', 'bar', 'timeline', 'kit'];
const rank = (name) => {
  const i = ORDER.indexOf(name);
  return i === -1 ? ORDER.length : i;
};
const tabs = TABS
  .map((t, i) => ({ t, i }))
  .sort((a, b) => rank(a.t.name) - rank(b.t.name) || a.i - b.i)
  .map(({ t }) => t);

// The clearance follows whether the bar is actually THERE.
//
// It used to be a static `:root` rule in this file's stylesheet, which applies
// the moment the component is loaded — so an app whose bar is hidden for having
// one destination would still reserve 56px at the bottom of every screen, and
// every toast would float above a gap.
//
// Set here rather than in a `:style` binding because it has to reach
// `ion-toast`, which Ionic presents at the app root and which is not a
// descendant of this component.
//
// 56px because app.css sets the bar to exactly 56px in BOTH modes (Ionic's
// iOS default is 50). Change one and you must change the other.
const hasBar = tabs.length > 1;
onMounted(() => {
  document.documentElement.style.setProperty('--tabbar-clearance', hasBar ? '56px' : '0px');
});
onBeforeUnmount(() => {
  document.documentElement.style.removeProperty('--tabbar-clearance');
});
</script>

<style>
/* THE SHELL DECLARES HOW MUCH OF THE BOTTOM IT HAS SPENT.
 *
 * An `ion-toast` is presented from the app root and knows nothing about this
 * bar, so without a number the install prompt and every undo bar land squarely
 * on top of the app's primary navigation — the tab buttons cannot be tapped
 * while a toast is over them.
 *
 * `tokens.css` declares `--tabbar-clearance: 0px` and `ionic.css` spends it on
 * `ion-toast`. This is the only file that knows the bar exists, so this is
 * where the value belongs: a shell with no bottom bar (stack, sidebar) leaves
 * it at zero and the rule costs nothing.
 *
 * The safe-area inset is NOT included — `ionic.css` adds it separately,
 * because on a home-indicator phone the bar is its buttons plus the inset
 * below them.
 *
 * SET FROM SCRIPT, not from this rule, because it is conditional: a bar is
 * only drawn above two destinations, and a static rule would reserve the space
 * whether or not the bar exists. See `hasBar` in the script block — it still
 * lands on the document element, for the ion-toast reason above.
 *
 * The bar's LOOK (ground, line, icons, mono labels) is in app.css with the
 * rest of the chrome. */
</style>
